/**
 * Computes the tab order after a drag moves `draggedId` next to `targetId`.
 * Removing then re-inserting keeps a single source of truth for direction: when
 * the dragged tab currently sits before the target it lands after it, and vice
 * versa. Pure so the reorder math is unit-testable.
 */
export function reorderTabs(
  current: readonly string[],
  draggedId: string,
  targetId: string,
): string[] {
  const movingRight = current.indexOf(draggedId) < current.indexOf(targetId);
  const next = current.filter((id) => id !== draggedId);
  const targetIndex = next.indexOf(targetId);
  const insertionIndex = targetIndex < 0 ? next.length : targetIndex + (movingRight ? 1 : 0);
  next.splice(insertionIndex, 0, draggedId);
  return next;
}

const TAB_SELECTOR = '[data-panel-tab-id]';

/**
 * Snapshots the on-screen position of every tab inside `container`, keyed by its
 * tab id — the "First" half of a FLIP animation. Scoped to the container (rather
 * than the whole document) so unrelated tab bars are never measured.
 */
export function captureTabPositions(container: HTMLElement | null): Map<string, DOMRect> {
  if (container === null) return new Map();
  return new Map(
    [...container.querySelectorAll<HTMLElement>(TAB_SELECTOR)].map((tab) => [
      tab.dataset['panelTabId'] ?? '',
      tab.getBoundingClientRect(),
    ]),
  );
}

/**
 * Plays the "Last/Invert/Play" half of a FLIP reorder: for every tab in
 * `container` (except the one being dragged), animates from its previously
 * captured position to its new layout position. Scoped to the container so the
 * query never reaches tabs outside this panel.
 */
export function animateTabReorder(
  container: HTMLElement | null,
  previous: Map<string, DOMRect>,
  draggedId: string,
): void {
  if (container === null) return;
  for (const tab of container.querySelectorAll<HTMLElement>(TAB_SELECTOR)) {
    const id = tab.dataset['panelTabId'];
    if (id === undefined || id === draggedId) continue;
    const previousRect = previous.get(id);
    if (previousRect === undefined) continue;
    const current = tab.getBoundingClientRect();
    const offset = previousRect.left - current.left;
    if (Math.abs(offset) < 1) continue;
    tab.animate([{ transform: `translateX(${offset}px)` }, { transform: 'translateX(0)' }], {
      duration: 180,
      easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    });
  }
}
